// import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
// import { AlertCircle } from "lucide-react";
// import { Button } from "@/components/ui/button";

// const ExitExamDialog = ({ open, onOpenChange, onContinue, onConfirm }) => (
//   <Dialog open={open} onOpenChange={onOpenChange}>
//    <DialogContent className="bg-white p-6 sm:p-8 rounded-2xl shadow-2xl w-[90%] max-w-[360px] sm:max-w-[480px] border-0">

//       <DialogHeader>
//         <div className="text-center space-y-4">
//           <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
//             <AlertCircle className="w-8 h-8 text-red-600" />
//           </div>
//           <DialogTitle className="text-lg font-semibold text-[#1C1F26] leading-[26px]">
//             Êtes-vous sûr(e) de vouloir quitter ?
//           </DialogTitle>
//           <p className="text-sm text-gray-600">
//             Votre progression actuelle ne sera pas sauvegardée.
//           </p>
//         </div>
//       </DialogHeader>
//       <DialogFooter className="flex flex-col sm:flex-row gap-3 pt-6">
//         <Button
//           onClick={onContinue}
//           variant="outline"
//           className="text-sm font-normal rounded-lg border border-gray-200 hover:bg-gray-50 flex-1"
//         >
//            Non, continuer l’examen
//         </Button>
//         <Button
//           onClick={onConfirm}
//           className="bg-[#FF5C5C] hover:bg-[#EF4444] text-white text-sm font-normal rounded-lg flex-1"
//         >
//           Oui, quitter
//         </Button>
//       </DialogFooter>
//     </DialogContent>
//   </Dialog>
// );

// export default ExitExamDialog;

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ExitExamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContinue: () => void;
  onConfirm: () => void;
}

const ExitExamDialog = ({ open, onOpenChange, onContinue, onConfirm }: ExitExamDialogProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (open) {
      // First render the portal
      setShouldRender(true);
      // Then trigger animation after a tiny delay
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      // Start closing animation
      setIsAnimating(false);
      // Remove from DOM after animation completes
      const timeout = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  if (!isMounted || !shouldRender) return null;

  const handleBackdropClick = () => onOpenChange(false);
  const handleContentClick: React.MouseEventHandler<HTMLDivElement> = (e) => e.stopPropagation();

  const modal = (
    <div
      className={`
        fixed inset-0 z-50 flex items-center justify-center px-4
        bg-black/40
        transition-opacity duration-200 ease-out
        ${isAnimating ? "opacity-100" : "opacity-0"}
      `}
      onClick={handleBackdropClick}
    >
      <div
        onClick={handleContentClick}
        role="dialog"
        aria-modal="true"
        className={`
          bg-white p-6 sm:p-8 rounded-2xl shadow-2xl w-full max-w-[360px] sm:max-w-[480px] border-0
          transform transition-transform duration-200 ease-out
          ${isAnimating ? "scale-100 translate-y-0" : "scale-95 translate-y-2"}
        `}
      >
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-lg font-semibold text-[#1C1F26] leading-[26px]">
            Êtes-vous sûr(e) de vouloir quitter ?
          </h2>
          <p className="text-sm text-gray-600">
            Votre progression actuelle ne sera pas sauvegardée.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 pt-6">
          <Button
            onClick={onContinue}
            className="
              flex-1
              text-sm font-medium
              rounded-lg
              border border-gray-200
              bg-[#F9FAFB]
              text-[#1C1F26]
              hover:bg-[#F3F4F6]
              hover:border-gray-300
              active:bg-[#E5E7EB]
            "
          >
            Non, continuer l'examen
          </Button>
          <Button
            onClick={onConfirm}
            className="bg-[#FF5C5C] hover:bg-[#EF4444] text-white text-sm font-normal rounded-lg flex-1"
          >
            Oui, quitter
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ExitExamDialog;
